import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getUserById,
  updateUser,
  updateUserSubscription,
  extendSubscription,
  cancelSubscription,
  addBonusGenerations,
  getUserActivity,
  getUserGenerations,
  getUserStats,
  deleteUser,
  setUserLicense,
  creditUser
} from '../../services/adminService';
import {
  ChevronLeft,
  User,
  Mail,
  Building,
  Calendar,
  Activity,
  CreditCard,
  TrendingUp,
  Edit2,
  Save,
  X,
  Plus,
  Trash2,
  RefreshCw,
  Clock
} from 'lucide-react';

interface UserDetails {
  user: {
    id: string;
    email: string;
    fullName: string;
    companyName: string;
    accountType: string;
    license?: 'free'|'commercial'|'template_pack'|'professional'|'agency';
    credits_balance?: number;
    role: string;
    subscriptionStatus: string;
    subscriptionExpiresAt: string;
    monthlyLimit: number;
    generationsThisMonth: number;
    avatarUrl: string;
    createdAt: string;
    updatedAt: string;
    lastSignInAt: string;
  };
  statistics: {
    totalGenerations: number;
    last30Days: number;
    templateUsage: Record<string, number>;
    averagePerDay: number;
  };
  recentMockups: any[];
  recentActivity: any[];
}

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    fullName: '',
    companyName: '',
    monthlyLimit: 0
  });
  const [subscriptionForm, setSubscriptionForm] = useState({
    accountType: '',
    expiresAt: '',
    extendDays: 30,
    bonusGenerations: 10
  });
  const [licenseForm, setLicenseForm] = useState<'free'|'commercial'|'template_pack'|'professional'|'agency'>('free');
  const [creditAmount, setCreditAmount] = useState<number>(100);

  useEffect(() => {
    if (id) {
      loadUserDetails();
    }
  }, [id]);

  const loadUserDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getUserById(id);
      setUserDetails(data);
      setEditForm({
        fullName: data.user.fullName || '',
        companyName: data.user.companyName || '',
        monthlyLimit: data.user.monthlyLimit
      });
      setSubscriptionForm({
        accountType: data.user.accountType,
        expiresAt: data.user.subscriptionExpiresAt ? data.user.subscriptionExpiresAt.split('T')[0] : '',
        extendDays: 30,
        bonusGenerations: 10
      });
      setLicenseForm((data.user.license as any) || 'free');
      console.log(`Loaded details for user ${id} [[memory:5998053]]`);
    } catch (err) {
      console.error('Error loading user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!id) return;
    
    try {
      await updateUser(id, {
        full_name: editForm.fullName,
        company_name: editForm.companyName,
        monthly_mockup_limit: editForm.monthlyLimit
      });
      setIsEditing(false);
      await loadUserDetails();
      console.log(`Updated user ${id} [[memory:5998053]]`);
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user');
    }
  };

  const handleUpdateSubscription = async () => {
    if (!id) return;
    
    try {
      await updateUserSubscription(id, {
        accountType: subscriptionForm.accountType,
        expiresAt: subscriptionForm.expiresAt
      });
      await loadUserDetails();
      console.log(`Updated subscription for user ${id} [[memory:5998053]]`);
      alert('Subscription updated successfully');
    } catch (err) {
      console.error('Error updating subscription:', err);
      alert('Failed to update subscription');
    }
  };

  const handleExtendSubscription = async () => {
    if (!id) return;
    
    try {
      await extendSubscription(id, subscriptionForm.extendDays);
      await loadUserDetails();
      console.log(`Extended subscription for user ${id} [[memory:5998053]]`);
      alert(`Subscription extended by ${subscriptionForm.extendDays} days`);
    } catch (err) {
      console.error('Error extending subscription:', err);
      alert('Failed to extend subscription');
    }
  };

  const handleCancelSubscription = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      await cancelSubscription(id, 'Admin cancelled');
      await loadUserDetails();
      console.log(`Cancelled subscription for user ${id} [[memory:5998053]]`);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      alert('Failed to cancel subscription');
    }
  };

  const handleAddBonusGenerations = async () => {
    if (!id) return;
    
    try {
      await addBonusGenerations(id, subscriptionForm.bonusGenerations);
      await loadUserDetails();
      console.log(`Added ${subscriptionForm.bonusGenerations} bonus generations for user ${id} [[memory:5998053]]`);
      alert(`Added ${subscriptionForm.bonusGenerations} bonus generations`);
    } catch (err) {
      console.error('Error adding bonus generations:', err);
      alert('Failed to add bonus generations');
    }
  };

  const handleDeleteUser = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await deleteUser(id);
      console.log(`Deleted user ${id} [[memory:5998053]]`);
      navigate('/admin/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'User not found'}</p>
          <Link to="/admin/users" className="text-indigo-600 hover:text-indigo-900">
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const { user, statistics, recentMockups, recentActivity } = userDetails;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/admin/users" className="mr-4">
                <ChevronLeft className="h-6 w-6 text-gray-600" />
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
            </div>
            <button
              onClick={handleDeleteUser}
              className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
            >
              <Trash2 className="-ml-1 mr-2 h-4 w-4" />
              Delete User
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdateUser}
                        className="text-green-600 hover:text-green-900"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditForm({
                            fullName: user.fullName || '',
                            companyName: user.companyName || '',
                            monthlyLimit: user.monthlyLimit
                          });
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.fullName}
                        onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.fullName || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{user.email}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Company</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.companyName}
                        onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{user.companyName || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Role</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'admin' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(user.createdAt)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Sign In</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDateTime(user.lastSignInAt)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Management */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Account Type</label>
                    <select
                      value={subscriptionForm.accountType}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, accountType: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800' :
                        user.subscriptionStatus === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.subscriptionStatus}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Expires At</label>
                    <input
                      type="date"
                      value={subscriptionForm.expiresAt}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, expiresAt: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <button
                    onClick={handleUpdateSubscription}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Update Subscription
                  </button>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={subscriptionForm.extendDays}
                          onChange={(e) => setSubscriptionForm({ ...subscriptionForm, extendDays: Number(e.target.value) })}
                          className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          min="1"
                        />
                        <button
                          onClick={handleExtendSubscription}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Extend Days
                        </button>
                      </div>

                      <button
                        onClick={handleCancelSubscription}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                      >
                        Cancel Subscription
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* License & Credits */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">License & Credits</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">License</label>
                    <select
                      value={licenseForm}
                      onChange={(e) => setLicenseForm(e.target.value as any)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="free">Free (+10)</option>
                      <option value="commercial">Commercial (+275)</option>
                      <option value="template_pack">Template Pack (+500)</option>
                      <option value="professional">Professional (+725)</option>
                      <option value="agency">Agency (+1100)</option>
                    </select>
                    <button
                      onClick={async () => { if (!id) return; await setUserLicense(id, licenseForm); await loadUserDetails(); }}
                      className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Apply License & Grant Credits
                    </button>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <label className="text-sm font-medium text-gray-500">Manual Credit</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(parseInt(e.target.value || '0', 10))}
                        className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-40"
                      />
                      <button
                        onClick={async () => { if (!id) return; await creditUser(id, creditAmount, 'admin_adjustment'); await loadUserDetails(); }}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Credit User
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Current balance: {userDetails?.user?.credits_balance ?? '—'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics and Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Usage Statistics */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Statistics</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Total Generations</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {statistics.totalGenerations}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Last 30 Days</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {statistics.last30Days}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">This Month</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {user.generationsThisMonth} / {isEditing ? (
                        <input
                          type="number"
                          value={editForm.monthlyLimit}
                          onChange={(e) => setEditForm({ ...editForm, monthlyLimit: Number(e.target.value) })}
                          className="w-20 inline border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          min="0"
                        />
                      ) : user.monthlyLimit}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-500">Avg per Day</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                      {statistics.averagePerDay.toFixed(1)}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={subscriptionForm.bonusGenerations}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, bonusGenerations: Number(e.target.value) })}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      min="1"
                    />
                    <button
                      onClick={handleAddBonusGenerations}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Plus className="-ml-1 mr-2 h-4 w-4" />
                      Add Bonus
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Template Usage */}
            {Object.keys(statistics.templateUsage).length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Template Usage</h3>
                  <div className="space-y-2">
                    {Object.entries(statistics.templateUsage)
                      .sort(([, a], [, b]) => b - a)
                      .map(([template, count]) => (
                        <div key={template} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{template}</span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Mockups */}
            {recentMockups.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Mockups</h3>
                  <div className="space-y-3">
                    {recentMockups.slice(0, 5).map((mockup) => (
                      <div key={mockup.id} className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{mockup.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(mockup.created_at)}
                          </p>
                        </div>
                        <span className="text-xs text-gray-600">
                          {mockup.downloads} downloads
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {recentActivity.slice(0, 10).map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3">
                        <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">
                            {activity.activity_type.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(activity.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
